import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { propertyDefinitions, propertyGroupOrder } from "../data/property-definitions"
import PropertyEditorResolver from "../PropertyEditorResolver"
import ClassNameEditor from "./ClassNameEditor"

const PropertyEditor = ({
  isOpen,
  onClose,
  currentClass,
  onClassUpdate,
  onClassMetadataUpdate,
  onClassCreate,
  isCreatingNew = false,
  hideHeader = false,
  existingClasses = []
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [isEditingClassName, setIsEditingClassName] = useState(isCreatingNew)

  // Ensure we start in editing mode when creating a new class
  React.useEffect(() => {
    if (isCreatingNew && isOpen) {
      setIsEditingClassName(true)
    }
  }, [isCreatingNew, isOpen])

  if (!isOpen) return null

  const className = currentClass?.name || ""
  const properties = currentClass?.properties || {}

  // Clean tag-based search system
  const searchMatches = (tags, searchTerm) => {
    if (!searchTerm || !tags) return true
    const searchLower = searchTerm.toLowerCase().trim()
    return tags.some(tag => tag.toLowerCase().includes(searchLower))
  }

  // Filter property groups based on search tags
  const getFilteredPropertyGroups = () => {
    if (!searchTerm) return propertyGroupOrder

    return propertyGroupOrder.filter(groupKey => {
      const group = propertyDefinitions[groupKey]
      if (!group) return false

      // Check if group tags match the search term
      return searchMatches(group.tags, searchTerm)
    })
  }


  // Get the appropriate property editor component using data-driven resolver
  const getPropertyEditor = (groupKey) => {
    const group = propertyDefinitions[groupKey]

    // Prepare editor props - modifiers editor needs different props
    const editorProps = group?.type === 'modifiers'
      ? {
          currentClass: currentClass,
          onModifierChange: onClassMetadataUpdate,
        }
      : {
          properties,
          onChange: onClassUpdate,
        }

    return PropertyEditorResolver.renderEditor(group, groupKey, editorProps)
  }

  const filteredGroups = getFilteredPropertyGroups()

  // If hideHeader is true, render only the properties without modal wrapper
  if (hideHeader) {
    return (
      <div className="flex-1 flex flex-col">
        {/* Property Search */}
        <div className="p-4 border-b border-admin_bordered bg-admin_paper">
          <Input
            variant="admin_compact"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
            showClearButton={true}
            autoFocus={true}
          />
        </div>

        {/* Properties Container */}
        <div className="flex-1 overflow-hidden">
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper pb-20">
            {filteredGroups.length === 0 ? (
              <div className="p-4 text-center text-sm text-admin_text_secondary">
                {searchTerm ? 'No properties found' : 'No properties available'}
              </div>
            ) : (
              <div className="divide-y divide-admin_bordered">
                {filteredGroups.map(groupKey => {
                  const group = propertyDefinitions[groupKey]
                  if (!group) return null

                  return (
                    <div key={groupKey}>
                      {getPropertyEditor(groupKey)}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_drawer_right"
      closeOnClickOutside={false}
      passInteractionEvents={true}
      title={isCreatingNew ? "Create New Class" : `Edit Class: .${className}`}
    >
      {/* Fixed Header Container */}
      <div className="flex-shrink-0 bg-admin_paper">
        {/* Class Name Section */}
        <div className="p-4 border-b border-admin_bordered bg-admin_paper">
          <ClassNameEditor
            className={className}
            isEditing={isEditingClassName}
            isCreatingNew={isCreatingNew}
            existingClasses={existingClasses}
            onEdit={() => setIsEditingClassName(true)}
            onSave={(newName) => {
              if (isCreatingNew) {
                onClassCreate?.(newName, properties)
              } else {
                // Update existing class name
                onClassUpdate?.({ ...currentClass, name: newName })
              }
              setIsEditingClassName(false)
            }}
            onCancel={() => {
              if (isCreatingNew) {
                onClose()
              } else {
                setIsEditingClassName(false)
              }
            }}
          />
        </div>

        {/* Property Search */}
        <div className="p-4 border-b border-admin_bordered bg-admin_paper">
          <Input
            variant="admin_compact"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
            showClearButton={true}
            autoFocus={!isCreatingNew}
          />
        </div>
      </div>

      {/* Scrollable Properties Container */}
      <div className="flex-1 overflow-hidden">
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper pb-20">
          {/* Show message for new class creation */}
          {isCreatingNew && isEditingClassName ? (
            <div className="p-8 text-center">
              <Typography variant="admin_h5" className="mb-2 text-admin_text_secondary">
                Create Your CSS Class
              </Typography>
              <Typography variant="admin_body_small" className="text-admin_text_secondary opacity-75">
                Enter a class name above and click "Save" to start editing CSS properties
              </Typography>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="p-4 text-center text-sm text-admin_text_secondary">
              {searchTerm ? 'No results found' : 'No properties available'}
            </div>
          ) : (
            <div className="divide-y divide-admin_bordered">
              {filteredGroups.map(groupKey => {
                const group = propertyDefinitions[groupKey]
                if (!group) return null

                return (
                  <div key={groupKey}>
                    {getPropertyEditor(groupKey)}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default PropertyEditor